/**
 * MealCatalog.js
 *
 * Small, hand-authored stand-in for the real Nutrition Database (UC-02
 * steps 4-6 don't have a backend yet). Each slot ("breakfast", "lunch",
 * etc.) has several candidate dishes instead of exactly one, each tagged
 * with the same vocabulary the assessment already collects:
 *
 *  - dietTypes: which of FoodPreferencesStep's DIET_TYPE_OPTIONS ids this
 *    dish is safe for (hard filter — see MealEngine.js's isMealEligible).
 *  - cuisineTags: overlaps with FoodPreferencesStep's CUISINE_OPTIONS ids
 *    (soft ranking, per FSD v2.1 §6 — never excludes a dish).
 *  - glutenFree: for MedicalInfoStep's "coeliac_gluten" hard exclusion
 *    (DEC-09: "Hard gluten exclusion if diagnosed/selected").
 *  - ingredients: `id` values reuse DislikesStep/LikesStep's
 *    FOOD_SEARCH_ITEMS ids where possible, so a disliked food actually
 *    matches and excludes the dish (see MealEngine.js).
 *
 * This is still a mock catalog, not a real database — macros are
 * reasonable estimates, not verified nutrition data, and the dish list
 * per slot is small (4-6) rather than exhaustive. But every dish here is
 * genuinely eligible/ineligible per user, unlike the single fixed dish
 * per slot this replaced.
 */

export const MEAL_CATALOG = {
  breakfast: [
    {
      id: "breakfast_poha",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Vegetable Poha",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: ["gujarati"],
      glutenFree: true,
      calories: 320,
      proteinG: 8,
      carbsG: 52,
      fatG: 9,
      ingredients: [
        { id: "poha", name: "Poha", substitutionChain: ["Poha", "Oats", "Quinoa"] },
        { id: "peanuts", name: "Peanuts", substitutionChain: ["Peanuts", "Cashews", "Almonds"] },
      ],
      recipeSteps: [
        "Rinse the poha until soft, then drain well.",
        "Sauté mustard seeds, curry leaves, onion, and peanuts until golden.",
        "Add the poha and turmeric, mix gently, and cook 3-4 minutes.",
        "Finish with lemon juice and coriander.",
      ],
    },
    {
      id: "breakfast_moong_dal_chilla",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Moong Dal Chilla",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 280,
      proteinG: 14,
      carbsG: 34,
      fatG: 8,
      ingredients: [{ id: "dal", name: "Moong Dal", substitutionChain: ["Dal", "Sprouts", "Soya Chunks"] }],
      recipeSteps: [
        "Soak and grind moong dal into a smooth batter with cumin and ginger.",
        "Spread thin on a hot griddle like a savory crepe.",
        "Cook both sides until lightly golden.",
      ],
    },
    {
      id: "breakfast_masala_oats",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Masala Oats",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: ["continental"],
      glutenFree: true,
      calories: 260,
      proteinG: 9,
      carbsG: 42,
      fatG: 6,
      ingredients: [
        { id: "oats", name: "Oats", substitutionChain: ["Oats", "Quinoa", "Poha"] },
        { id: "spinach", name: "Spinach", substitutionChain: ["Spinach", "Broccoli", "Mushroom"] },
      ],
      recipeSteps: [
        "Dry roast oats briefly, then simmer in water with chopped vegetables.",
        "Season with cumin and black pepper; cook until creamy.",
      ],
    },
    {
      id: "breakfast_egg_bhurji_toast",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Egg Bhurji with Toast",
      dietTypes: ["eggetarian", "non_vegetarian"],
      cuisineTags: ["continental"],
      glutenFree: false,
      calories: 340,
      proteinG: 18,
      carbsG: 30,
      fatG: 14,
      ingredients: [
        { id: "eggs", name: "Eggs", substitutionChain: ["Eggs", "Tofu", "Paneer"] },
        { id: "bread", name: "Bread", substitutionChain: ["Bread", "Oats", "Poha"] },
      ],
      recipeSteps: [
        "Scramble eggs with sautéed onion, tomato, and green chili.",
        "Toast the bread and serve alongside.",
      ],
    },
    {
      id: "breakfast_chicken_egg_wrap",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Grilled Chicken & Egg Wrap",
      dietTypes: ["non_vegetarian"],
      cuisineTags: ["continental"],
      glutenFree: false,
      calories: 380,
      proteinG: 26,
      carbsG: 32,
      fatG: 14,
      ingredients: [
        { id: "chicken", name: "Chicken", substitutionChain: ["Chicken", "Fish", "Eggs"] },
        { id: "bread", name: "Wrap", substitutionChain: ["Bread", "Roti", "Rice"] },
      ],
      recipeSteps: [
        "Grill seasoned chicken strips until cooked through.",
        "Scramble an egg, layer both into a wrap with salad.",
      ],
    },
    {
      id: "breakfast_idli_sambar",
      slotLabel: "Breakfast",
      defaultTime: "08:00",
      name: "Idli Sambar",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: ["south_indian"],
      glutenFree: true,
      calories: 300,
      proteinG: 10,
      carbsG: 56,
      fatG: 5,
      ingredients: [
        { id: "idli", name: "Idli", substitutionChain: ["Idli", "Dosa", "Upma"] },
        { id: "sambar", name: "Sambar", substitutionChain: ["Sambar", "Dal", "Rajma"] },
      ],
      recipeSteps: ["Steam idlis until fluffy.", "Serve hot with sambar and coconut chutney."],
    },
  ],

  mid_morning_snack: [
    {
      id: "mms_sprouts_chaat",
      slotLabel: "Mid-Morning Snack",
      defaultTime: "10:30",
      name: "Sprouts Chaat",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: [],
      glutenFree: true,
      calories: 150,
      proteinG: 8,
      carbsG: 18,
      fatG: 4,
      ingredients: [{ id: "sprouts", name: "Sprouts", substitutionChain: ["Sprouts", "Roasted Chana", "Fruit"] }],
      recipeSteps: ["Toss boiled sprouts with lemon, onion, and chaat masala.", "Serve chilled."],
    },
    {
      id: "mms_roasted_makhana",
      slotLabel: "Mid-Morning Snack",
      defaultTime: "10:30",
      name: "Roasted Makhana",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 130,
      proteinG: 4,
      carbsG: 16,
      fatG: 6,
      ingredients: [{ id: "nuts", name: "Makhana", substitutionChain: ["Nuts", "Banana", "Apple"] }],
      recipeSteps: ["Dry roast makhana with a little ghee and black pepper until crisp."],
    },
    {
      id: "mms_fruit_bowl",
      slotLabel: "Mid-Morning Snack",
      defaultTime: "10:30",
      name: "Seasonal Fruit Bowl",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 110,
      proteinG: 2,
      carbsG: 26,
      fatG: 1,
      ingredients: [{ id: "banana", name: "Banana", substitutionChain: ["Banana", "Apple", "Mango"] }],
      recipeSteps: ["Chop and mix seasonal fruit; serve as-is."],
    },
    {
      id: "mms_paneer_cubes",
      slotLabel: "Mid-Morning Snack",
      defaultTime: "10:30",
      name: "Pan-Seared Paneer Cubes",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 180,
      proteinG: 12,
      carbsG: 6,
      fatG: 12,
      ingredients: [{ id: "paneer", name: "Paneer", substitutionChain: ["Paneer", "Tofu", "Soya Chunks"] }],
      recipeSteps: ["Pan-sear paneer cubes with a pinch of chaat masala."],
    },
    {
      id: "mms_boiled_eggs",
      slotLabel: "Mid-Morning Snack",
      defaultTime: "10:30",
      name: "Boiled Eggs",
      dietTypes: ["eggetarian", "non_vegetarian"],
      cuisineTags: [],
      glutenFree: true,
      calories: 140,
      proteinG: 12,
      carbsG: 2,
      fatG: 10,
      ingredients: [{ id: "eggs", name: "Eggs", substitutionChain: ["Eggs", "Paneer", "Tofu"] }],
      recipeSteps: ["Boil eggs 8-10 minutes.", "Season with black pepper and a pinch of salt."],
    },
  ],

  lunch: [
    {
      id: "lunch_dal_rice_spinach",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Dal, Rice & Sautéed Spinach",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 520,
      proteinG: 22,
      carbsG: 78,
      fatG: 12,
      ingredients: [
        { id: "dal", name: "Dal", substitutionChain: ["Dal", "Rajma", "Chole", "Sprouts"] },
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Quinoa", "Roti"] },
        { id: "spinach", name: "Spinach", substitutionChain: ["Spinach", "Broccoli", "Mushroom"] },
      ],
      recipeSteps: [
        "Pressure-cook the dal with turmeric until soft.",
        "Temper with cumin, garlic, and dried red chili; stir into the dal.",
        "Sauté spinach with garlic 3-4 minutes.",
        "Serve with steamed rice.",
      ],
    },
    {
      id: "lunch_rajma_chawal",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Rajma Chawal",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: ["punjabi"],
      glutenFree: true,
      calories: 540,
      proteinG: 20,
      carbsG: 82,
      fatG: 10,
      ingredients: [
        { id: "rajma", name: "Rajma", substitutionChain: ["Rajma", "Chole", "Dal"] },
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Quinoa", "Roti"] },
      ],
      recipeSteps: [
        "Pressure-cook soaked rajma until soft.",
        "Simmer in a tomato-onion masala 15-20 minutes.",
        "Serve over steamed rice.",
      ],
    },
    {
      id: "lunch_jain_khichdi",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Khichdi (no onion/garlic) with Cucumber Salad",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 480,
      proteinG: 16,
      carbsG: 76,
      fatG: 10,
      ingredients: [
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Quinoa"] },
        { id: "dal", name: "Dal", substitutionChain: ["Dal", "Sprouts"] },
      ],
      recipeSteps: [
        "Cook rice and dal together with turmeric and cumin — no onion or garlic.",
        "Serve with a simple cucumber-tomato salad.",
      ],
    },
    {
      id: "lunch_chicken_curry_rice",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Chicken Curry with Rice",
      dietTypes: ["non_vegetarian"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 560,
      proteinG: 34,
      carbsG: 60,
      fatG: 16,
      ingredients: [
        { id: "chicken", name: "Chicken", substitutionChain: ["Chicken", "Fish", "Eggs"] },
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Roti"] },
      ],
      recipeSteps: ["Simmer chicken in a tomato-onion masala until cooked through.", "Serve with steamed rice."],
    },
    {
      id: "lunch_fish_curry_rice",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Fish Curry with Rice",
      dietTypes: ["non_vegetarian"],
      cuisineTags: ["south_indian"],
      glutenFree: true,
      calories: 540,
      proteinG: 32,
      carbsG: 58,
      fatG: 14,
      ingredients: [
        { id: "fish", name: "Fish", substitutionChain: ["Fish", "Chicken", "Eggs"] },
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Roti"] },
      ],
      recipeSteps: ["Simmer fish in a tangy coconut-based curry.", "Serve with steamed rice."],
    },
    {
      id: "lunch_paneer_roti",
      slotLabel: "Lunch",
      defaultTime: "13:00",
      name: "Paneer Curry with Roti",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: ["punjabi"],
      glutenFree: false,
      calories: 560,
      proteinG: 24,
      carbsG: 64,
      fatG: 20,
      ingredients: [
        { id: "paneer", name: "Paneer", substitutionChain: ["Paneer", "Tofu", "Soya Chunks"] },
        { id: "roti", name: "Roti", substitutionChain: ["Roti", "Rice", "Paratha"] },
      ],
      recipeSteps: ["Simmer paneer cubes in a tomato-based gravy.", "Serve hot with roti."],
    },
  ],

  afternoon_snack: [
    {
      id: "as_veg_soup",
      slotLabel: "Afternoon Snack",
      defaultTime: "16:30",
      name: "Vegetable Soup",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: ["continental"],
      glutenFree: true,
      calories: 120,
      proteinG: 4,
      carbsG: 18,
      fatG: 3,
      ingredients: [
        { id: "spinach", name: "Spinach", substitutionChain: ["Spinach", "Broccoli"] },
        { id: "broccoli", name: "Broccoli", substitutionChain: ["Broccoli", "Mushroom", "Spinach"] },
      ],
      recipeSteps: ["Simmer chopped vegetables in a light broth until soft.", "Season with black pepper."],
    },
    {
      id: "as_roasted_chana",
      slotLabel: "Afternoon Snack",
      defaultTime: "16:30",
      name: "Roasted Chana",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 140,
      proteinG: 8,
      carbsG: 20,
      fatG: 3,
      ingredients: [{ id: "chole", name: "Roasted Chana", substitutionChain: ["Chole", "Rajma", "Sprouts"] }],
      recipeSteps: ["Dry roast boiled chickpeas with chaat masala until lightly crisp."],
    },
    {
      id: "as_fruit_chaat",
      slotLabel: "Afternoon Snack",
      defaultTime: "16:30",
      name: "Fruit Chaat",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 110,
      proteinG: 2,
      carbsG: 26,
      fatG: 1,
      ingredients: [
        { id: "apple", name: "Apple", substitutionChain: ["Apple", "Banana"] },
        { id: "mango", name: "Mango", substitutionChain: ["Mango", "Banana", "Apple"] },
      ],
      recipeSteps: ["Toss chopped fruit with a pinch of chaat masala and lemon juice."],
    },
    {
      id: "as_paneer_tikka_snack",
      slotLabel: "Afternoon Snack",
      defaultTime: "16:30",
      name: "Paneer Tikka Bites",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 200,
      proteinG: 14,
      carbsG: 8,
      fatG: 13,
      ingredients: [{ id: "paneer", name: "Paneer", substitutionChain: ["Paneer", "Tofu", "Soya Chunks"] }],
      recipeSteps: ["Marinate paneer cubes briefly, then pan-sear or grill until lightly charred."],
    },
    {
      id: "as_egg_whites",
      slotLabel: "Afternoon Snack",
      defaultTime: "16:30",
      name: "Seasoned Egg Whites",
      dietTypes: ["eggetarian", "non_vegetarian"],
      cuisineTags: [],
      glutenFree: true,
      calories: 130,
      proteinG: 14,
      carbsG: 2,
      fatG: 6,
      ingredients: [{ id: "eggs", name: "Eggs", substitutionChain: ["Eggs", "Paneer", "Tofu"] }],
      recipeSteps: ["Boil eggs, discard yolks, season whites with black pepper and herbs."],
    },
  ],

  dinner: [
    {
      id: "dinner_paneer_tikka_salad",
      slotLabel: "Dinner",
      defaultTime: "20:00",
      name: "Paneer Tikka with Salad",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: ["north_indian", "continental"],
      glutenFree: true,
      calories: 480,
      proteinG: 28,
      carbsG: 24,
      fatG: 26,
      ingredients: [
        { id: "paneer", name: "Paneer", substitutionChain: ["Paneer", "Tofu", "Soya Chunks"] },
        { id: "salad", name: "Mixed Salad", substitutionChain: ["Salad", "Sprouts", "Broccoli"] },
      ],
      recipeSteps: [
        "Marinate paneer cubes in yogurt and spices 20 minutes.",
        "Skewer with peppers and onion, grill or pan-sear until charred.",
        "Toss a simple salad with lemon and olive oil.",
      ],
    },
    {
      id: "dinner_grilled_tofu_bowl",
      slotLabel: "Dinner",
      defaultTime: "20:00",
      name: "Grilled Tofu Bowl",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: ["chinese"],
      glutenFree: true,
      calories: 460,
      proteinG: 26,
      carbsG: 30,
      fatG: 20,
      ingredients: [
        { id: "tofu", name: "Tofu", substitutionChain: ["Tofu", "Paneer", "Soya Chunks"] },
        { id: "broccoli", name: "Broccoli", substitutionChain: ["Broccoli", "Spinach", "Mushroom"] },
      ],
      recipeSteps: ["Pan-sear marinated tofu cubes until golden.", "Stir-fry broccoli, combine into a bowl."],
    },
    {
      id: "dinner_grilled_chicken_veg",
      slotLabel: "Dinner",
      defaultTime: "20:00",
      name: "Grilled Chicken with Vegetables",
      dietTypes: ["non_vegetarian"],
      cuisineTags: ["continental"],
      glutenFree: true,
      calories: 520,
      proteinG: 38,
      carbsG: 20,
      fatG: 22,
      ingredients: [
        { id: "chicken", name: "Chicken", substitutionChain: ["Chicken", "Fish", "Eggs"] },
        { id: "broccoli", name: "Broccoli", substitutionChain: ["Broccoli", "Spinach"] },
      ],
      recipeSteps: ["Grill seasoned chicken breast until cooked through.", "Roast or steam vegetables alongside."],
    },
    {
      id: "dinner_chole_roti",
      slotLabel: "Dinner",
      defaultTime: "20:00",
      name: "Chole with Roti",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: ["north_indian"],
      glutenFree: false,
      calories: 520,
      proteinG: 20,
      carbsG: 70,
      fatG: 14,
      ingredients: [
        { id: "chole", name: "Chole", substitutionChain: ["Chole", "Rajma", "Dal"] },
        { id: "roti", name: "Roti", substitutionChain: ["Roti", "Rice", "Paratha"] },
      ],
      recipeSteps: ["Simmer chickpeas in a tomato-onion masala until thick.", "Serve hot with roti."],
    },
    {
      id: "dinner_khichdi_curd",
      slotLabel: "Dinner",
      defaultTime: "20:00",
      name: "Khichdi with Curd",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: ["north_indian"],
      glutenFree: true,
      calories: 440,
      proteinG: 16,
      carbsG: 66,
      fatG: 10,
      ingredients: [
        { id: "rice", name: "Rice", substitutionChain: ["Rice", "Quinoa"] },
        { id: "dal", name: "Dal", substitutionChain: ["Dal", "Sprouts"] },
        { id: "curd", name: "Curd", substitutionChain: ["Curd", "Milk"] },
      ],
      recipeSteps: ["Cook rice and dal together into a soft khichdi.", "Serve with a bowl of curd."],
    },
  ],

  bedtime_snack: [
    {
      id: "bts_warm_milk_almonds",
      slotLabel: "Bedtime Snack",
      defaultTime: "22:00",
      name: "Warm Milk with Almonds",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 140,
      proteinG: 6,
      carbsG: 12,
      fatG: 7,
      ingredients: [{ id: "milk", name: "Milk", substitutionChain: ["Milk", "Curd", "Herbal Tea"] }],
      recipeSteps: ["Warm the milk gently.", "Stir in crushed almonds and a pinch of cinnamon."],
    },
    {
      id: "bts_herbal_tea_nuts",
      slotLabel: "Bedtime Snack",
      defaultTime: "22:00",
      name: "Herbal Tea with Nuts",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 90,
      proteinG: 3,
      carbsG: 8,
      fatG: 5,
      ingredients: [{ id: "nuts", name: "Nuts", substitutionChain: ["Nuts", "Banana"] }],
      recipeSteps: ["Steep a caffeine-free herbal tea.", "Serve with a small handful of nuts."],
    },
    {
      id: "bts_small_curd_bowl",
      slotLabel: "Bedtime Snack",
      defaultTime: "22:00",
      name: "Small Bowl of Curd",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "jain"],
      cuisineTags: [],
      glutenFree: true,
      calories: 100,
      proteinG: 6,
      carbsG: 8,
      fatG: 4,
      ingredients: [{ id: "curd", name: "Curd", substitutionChain: ["Curd", "Milk"] }],
      recipeSteps: ["Serve a small bowl of plain curd, optionally with a pinch of roasted cumin."],
    },
    {
      id: "bts_soy_milk_nuts",
      slotLabel: "Bedtime Snack",
      defaultTime: "22:00",
      name: "Soy Milk with Nuts",
      dietTypes: ["vegetarian", "non_vegetarian", "eggetarian", "vegan"],
      cuisineTags: [],
      glutenFree: true,
      calories: 120,
      proteinG: 5,
      carbsG: 10,
      fatG: 6,
      ingredients: [{ id: "nuts", name: "Nuts", substitutionChain: ["Nuts", "Banana"] }],
      recipeSteps: ["Warm soy milk gently.", "Serve with a small handful of nuts."],
    },
  ],
};

// Chronological order — mirrors LoadingScreen.js's slot sequencing.
export const MEAL_SLOT_ORDER = ["breakfast", "mid_morning_snack", "lunch", "afternoon_snack", "dinner", "bedtime_snack"];
